import React from "react";
import { Phone, Mail, MapPin, Clock, MessageCircle } from "lucide-react";
import Seo from "@/components/Seo";
import Breadcrumbs from "@/components/Breadcrumbs";
import AppointmentForm from "@/components/AppointmentForm";
import InsurersStrip from "@/components/InsurersStrip";
import { CLINIC } from "@/lib/clinicInfo";

export default function Contact() {
  return (
    <>
      <Seo
        title="Contact Nehchal Eye Centre — Sector 32 Noida | Book Appointment"
        description="Book an appointment at Nehchal Eye Centre, 10 Marigold Court, Wave City Centre, Sector 32 Noida. Call +91 97115 11001 or WhatsApp us."
        path="/contact"
      />
      <Breadcrumbs items={[{ name: "Contact", path: "/contact" }]} />

      <section className="container-wide pt-10 pb-16">
        <div className="max-w-2xl">
          <div className="text-xs uppercase tracking-widest text-accent mb-3">Get in touch</div>
          <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl text-foreground leading-[1.05]">
            Book your appointment.
          </h1>
          <p className="mt-6 text-foreground/70 leading-7">
            Reach us by phone, WhatsApp, or the form below. For emergency eye concerns please call immediately.
          </p>
        </div>
      </section>

      <section className="container-wide pb-16 grid lg:grid-cols-5 gap-10">
        <div className="lg:col-span-2 space-y-6">
          <div className="rounded-2xl border border-border bg-card p-6">
            <div className="text-xs uppercase tracking-widest text-accent mb-3">Contact Information</div>
            <ul className="space-y-4 text-sm">
              <li className="flex items-start gap-3">
                <MapPin className="h-5 w-5 text-primary mt-0.5" />
                <div>
                  <div className="font-medium">Address</div>
                  <div className="text-foreground/75">{CLINIC.address.line1}</div>
                  <div className="text-foreground/75">{CLINIC.address.line2}, {CLINIC.address.state}</div>
                </div>
              </li>
              <li className="flex items-start gap-3">
                <Phone className="h-5 w-5 text-primary mt-0.5" />
                <div>
                  <div className="font-medium">Phone</div>
                  <a href={`tel:${CLINIC.phoneRaw}`} className="text-primary hover:opacity-80">{CLINIC.phone}</a>
                </div>
              </li>
              <li className="flex items-start gap-3">
                <Mail className="h-5 w-5 text-primary mt-0.5" />
                <div>
                  <div className="font-medium">Email</div>
                  <a href={`mailto:${CLINIC.email}`} className="text-primary hover:opacity-80">{CLINIC.email}</a>
                </div>
              </li>
              <li className="flex items-start gap-3">
                <Clock className="h-5 w-5 text-primary mt-0.5" />
                <div>
                  <div className="font-medium">Operating Hours</div>
                  <div className="text-foreground/75">Monday – Saturday: 9:00 AM – 7:00 PM</div>
                  <div className="text-foreground/75">Sunday: 10:00 AM – 1:00 PM</div>
                </div>
              </li>
            </ul>
            <div className="mt-6 flex flex-wrap gap-3">
              <a
                href={`tel:${CLINIC.phoneRaw}`}
                className="inline-flex items-center gap-2 rounded-full bg-primary text-primary-foreground px-4 py-2 text-sm hover:bg-primary/90"
                data-testid="contact-call-btn"
              >
                <Phone className="h-4 w-4" /> Call Now
              </a>
              <a
                href={CLINIC.whatsappUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 rounded-full border border-border px-4 py-2 text-sm hover:bg-secondary"
                data-testid="contact-whatsapp-btn"
              >
                <MessageCircle className="h-4 w-4" /> WhatsApp
              </a>
            </div>
          </div>

          <div className="rounded-2xl overflow-hidden border border-border bg-card">
            <iframe
              title="Nehchal Eye Centre Location Map"
              src={CLINIC.mapsEmbedUrl}
              className="w-full h-72 border-0"
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
              allowFullScreen
              data-testid="contact-map"
            />
          </div>
        </div>

        <div className="lg:col-span-3">
          <div className="rounded-2xl border border-border bg-card p-6 sm:p-8">
            <div className="text-xs uppercase tracking-widest text-accent mb-3">Request Appointment</div>
            <h2 className="font-serif text-2xl mb-6">Tell us how we can help.</h2>
            <AppointmentForm />
          </div>
        </div>
      </section>

      <InsurersStrip compact />
    </>
  );
}
